import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { Target, CheckCircle2, Clock, IndianRupee } from "lucide-react";

const FIELDS = [
  { k: "calls", label: "Calls" }, { k: "meetings", label: "Meetings" },
  { k: "deals", label: "Deals" }, { k: "revenue", label: "Revenue (₹)" },
];

function SetterForm({ reps, onSaved }) {
  const [f, setF] = useState({ rep_id: "", period_type: "monthly", period_key: "", calls: "", meetings: "", deals: "", revenue: "" });
  const submit = async () => {
    for (const x of ["rep_id", "period_key", ...FIELDS.map((x) => x.k)]) if (f[x] === "" || f[x] === null) { toast.error("All fields are mandatory"); return; }
    try {
      await api.post("/targets", { ...f, calls: +f.calls, meetings: +f.meetings, deals: +f.deals, revenue: +f.revenue });
      toast.success("Targets set — sent to rep for sign-off");
      setF({ rep_id: "", period_type: "monthly", period_key: "", calls: "", meetings: "", deals: "", revenue: "" });
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const inp = "border border-[#DFDAD0] px-2 py-2 text-sm outline-none focus:border-[#1C2530]";
  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="target-setter">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60 mb-3">Set per-rep targets · rep must sign off</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
        <select value={f.rep_id} onChange={(e) => setF({ ...f, rep_id: e.target.value })} data-testid="target-rep" className={inp + " bg-white"}>
          <option value="">Select rep…</option>
          {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select value={f.period_type} onChange={(e) => setF({ ...f, period_type: e.target.value })} data-testid="target-period-type" className={inp + " bg-white"}>
          <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
        </select>
        <input value={f.period_key} onChange={(e) => setF({ ...f, period_key: e.target.value })} placeholder={f.period_type === "monthly" ? "2026-07" : "2026-Q3"} data-testid="target-period-key" className={inp} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        {FIELDS.map((x) => <input key={x.k} type="number" min="0" value={f[x.k]} onChange={(e) => setF({ ...f, [x.k]: e.target.value })} placeholder={x.label} data-testid={`target-${x.k}`} className={inp} />)}
      </div>
      <button onClick={submit} data-testid="target-save" className="bg-[#1C2530] text-[#F5F3EE] px-5 py-2 text-sm font-medium hover:bg-[#2a3644]">Set targets</button>
    </div>
  );
}

export default function Targets() {
  const { user } = useAuth();
  const isLead = user.role === "supervisor" || user.role === "admin";
  const [reps, setReps] = useState([]);
  const [rows, setRows] = useState(null);
  const load = () => api.get("/targets").then((r) => setRows(r.data));
  useEffect(() => { load(); if (isLead) api.get("/users").then((r) => setReps(r.data.filter((u) => u.role === "rep"))).catch(() => {}); }, []);

  const ack = async (t) => {
    try { await api.post(`/targets/${t.id}/acknowledge`); toast.success("Targets acknowledged — you're confirmed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <>
      <PageHeader eyebrow="PER-REP TARGETS & SIGN-OFF" title="Target Setting" hint="Set each rep's outcome targets per period; reps acknowledge them so both sides agree on the number. Actuals show on Productivity and the Command Center." />
      <div className="p-8 space-y-5" data-testid="targets-page">
        {isLead && <SetterForm reps={reps} onSaved={load} />}
        {!rows ? <Loading /> : rows.length === 0 ? <Empty label="NO_TARGETS_SET" /> : (
          <div className="border border-[#DFDAD0] bg-white overflow-hidden" data-testid="targets-list">
            <table className="w-full text-sm">
              <thead><tr className="bg-[#EFECE5] font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                <th className="text-left px-3 py-2.5">Rep</th><th className="text-left px-3 py-2.5">Period</th>
                {FIELDS.map((x) => <th key={x.k} className="text-left px-3 py-2.5">{x.label}</th>)}
                <th className="text-left px-3 py-2.5">Status</th><th className="text-right px-3 py-2.5"></th>
              </tr></thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-t border-[#DFDAD0]" data-testid={`target-row-${t.id}`}>
                    <td className="px-3 py-2 font-medium text-[#1C2530]">{t.rep_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{t.period_type} · {t.period_key}</td>
                    {FIELDS.map((x) => <td key={x.k} className="px-3 py-2 font-mono tabular-nums text-xs">{x.k === "revenue" ? `₹${(t.revenue || 0).toLocaleString("en-IN")}` : t[x.k]}</td>)}
                    <td className="px-3 py-2">
                      {t.status === "confirmed"
                        ? <span className="flex items-center gap-1 font-mono text-[10px] uppercase text-[#2F7A3E]" data-testid={`target-status-${t.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Confirmed</span>
                        : <span className="flex items-center gap-1 font-mono text-[10px] uppercase text-[#E2711D]" data-testid={`target-status-${t.id}`}><Clock className="w-3.5 h-3.5" /> Pending Rep Sign-Off</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {user.role === "rep" && t.status === "pending_signoff" &&
                        <button onClick={() => ack(t)} data-testid={`target-ack-${t.id}`} className="bg-[#2F7A3E] text-white px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#256030]">Acknowledge</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
